/**
 * BuildJob - M3.1 async build job types.
 *
 * Represents a build job that is queued via Cloudflare Queue and executed
 * by the Node Build Worker. The job tracks its lifecycle through a strict
 * state machine and records all metrics for auditability.
 */

/** Types of build jobs the system can process. */
export type BuildJobType = 'daily_factory_run' | 'single_game_build' | 'repair';

/**
 * Build job status states.
 *
 * Happy path: QUEUED -> BUILDING -> TESTING -> READY_FOR_APPROVAL -> APPROVED -> PUBLISHED
 * Failure paths: BUILDING -> BUILD_FAILED (retry -> QUEUED)
 *                TESTING -> QA_FAILED -> REPAIRING -> TESTING (loop, <= 3)
 *                Exceeded retries -> REJECTED
 */
export enum BuildJobStatus {
  QUEUED = 'QUEUED',
  BUILDING = 'BUILDING',
  TESTING = 'TESTING',
  REPAIRING = 'REPAIRING',
  READY_FOR_APPROVAL = 'READY_FOR_APPROVAL',
  BUILD_FAILED = 'BUILD_FAILED',
  QA_FAILED = 'QA_FAILED',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  PUBLISHED = 'PUBLISHED',
}

/** Error codes for build job failures. */
export enum BuildJobErrorCode {
  BUILD_COMPILE_ERROR = 'BUILD_COMPILE_ERROR',
  BUILD_VALIDATION_ERROR = 'BUILD_VALIDATION_ERROR',
  BUILD_PACKAGE_ERROR = 'BUILD_PACKAGE_ERROR',
  QA_STATIC_ANALYSIS_FAILED = 'QA_STATIC_ANALYSIS_FAILED',
  QA_TEST_FAILED = 'QA_TEST_FAILED',
  REPAIR_EXHAUSTED = 'REPAIR_EXHAUSTED',
  LLM_RATE_LIMIT = 'LLM_RATE_LIMIT',
  LLM_TIMEOUT = 'LLM_TIMEOUT',
  LLM_INVALID_RESPONSE = 'LLM_INVALID_RESPONSE',
  HUNTER_NO_CANDIDATES = 'HUNTER_NO_CANDIDATES',
  ARTIFACT_UPLOAD_FAILED = 'ARTIFACT_UPLOAD_FAILED',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

/** A build job record as stored in D1. */
export interface BuildJob {
  job_id: string;
  type: BuildJobType;
  game_id?: string;
  version?: string;
  status: BuildJobStatus;
  attempt: number;
  max_attempts: number;
  llm_requests: number;
  llm_tokens: number;
  repair_count: number;
  error_code?: BuildJobErrorCode;
  error_message?: string;
  artifact_url?: string;
  checksum?: string;
  candidate_count?: number;
  selected_idea?: string;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  created_by: string;
}

/** Input for creating a new build job. */
export interface BuildJobCreateInput {
  type: BuildJobType;
  game_id?: string;
  version?: string;
  candidate_count?: number;
  created_by?: string;
}

/** Message payload sent over Cloudflare Queue. */
export interface BuildJobMessage {
  job_id: string;
  type: BuildJobType;
  game_id?: string;
  version?: string;
  candidate_count?: number;
  attempt: number;
  created_at: string;
}

/** Update fields for a build job (partial). */
export type BuildJobUpdate = Partial<
  Pick<
    BuildJob,
    | 'status'
    | 'attempt'
    | 'llm_requests'
    | 'llm_tokens'
    | 'repair_count'
    | 'error_code'
    | 'error_message'
    | 'artifact_url'
    | 'checksum'
    | 'game_id'
    | 'version'
    | 'selected_idea'
    | 'started_at'
    | 'finished_at'
  >
>;

/** Summary of a build job for list views. */
export interface BuildJobSummary {
  job_id: string;
  type: BuildJobType;
  status: BuildJobStatus;
  game_id?: string;
  version?: string;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  duration_ms?: number;
}
